import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { MemoryService } from '@services/memory/memory.service';
import { DataTableComponent, DataTableEvent } from '../../../../components/data-table/data-table.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DialogService } from '@services/dialog/dialog.service';
import { CompositionWizardCustomerSelectComponent } from './composition-wizard-customer-select/composition-wizard-customer-select.component';
import { OrganisationService } from '@services/organisations/organisation.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { MyOrganisationService } from '@services/organisations/my-organisation.service';
import { EntranceTypeByType } from './entrance-type.map';
import { Router } from '@angular/router';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { ProductService } from '@services/products/product.service';

@Component({
  selector: 'composition-wizard-dynamic-entrance',
  templateUrl: './composition-wizard-dynamic-entrance.component.html',
  styleUrls: ['./composition-wizard-dynamic-entrance.component.scss'],
  providers: [DynamicContentEntranceService, DynamicContentService, OrganisationService, PackageInstalledAppsService, MyOrganisationService, ProductService],
  standalone: false
})
export class CompositionWizardDynamicEntranceComponent implements OnInit {

  @Input() composition: Models.Composition;
  @ViewChild('dynamicContentTable') dynamicContentTable: DataTableComponent;
  @Output() save: EventEmitter<string> = new EventEmitter();

  public assignedContentEntranceService: MemoryService<Models.DynamicContentEntrance>;
  public columns = [{ prop: '_id' }, { prop: 'title' }, { prop: 'type' }, { prop: 'OwnerOrganisationID' }, { prop: 'OwnerCustomerID' }, {
    prop: 'children.length',
    name: 'Children'
  }];
  private dynamicContentEntrances: Models.DynamicContentEntrance[];

  headerButtons = [{ icon: 'add', callback: () => this.updateOrCreateDynamicContent() }];

  constructor(
    private dynamicContentEntranceService: DynamicContentEntranceService, private dynamicContentService: DynamicContentService, private organisationService: OrganisationService,
    private packageInstalledAppsService: PackageInstalledAppsService, private productService: ProductService, private messengerService: MessengerService,
    private myOrganisationService: MyOrganisationService, private dialogService: DialogService, private dialog: MatDialog, private router: Router, private snackbar: SnackbarService
  ) {
  }

  ngOnInit() {
    this.dynamicContentEntrances = [];
    this.assignedContentEntranceService = new MemoryService(this.dynamicContentEntrances);
    this.getEntrances();
  }

  public async updateOrCreateDynamicContent(entrance = null) {
    const result = await this.openConfigurationDialog(entrance);
    if (!result || !result.entranceType) {
      return;
    }
    const entranceType = EntranceTypeByType.get(result.entranceType);
    let dynamicContentEntrance = <Models.DynamicContentEntrance>Models.ModelFactory.createModelInstanceByName('DynamicContentEntrance');
    dynamicContentEntrance = entrance ?? dynamicContentEntrance;
    dynamicContentEntrance.title = entranceType.title;
    dynamicContentEntrance.type = entranceType.type;
    dynamicContentEntrance.readonly = result.readonly;
    dynamicContentEntrance.noCategories = result.noCategories;
    dynamicContentEntrance.config = result.config;
    if (!result.customerId) {
      return this.createOrUpdate(dynamicContentEntrance);
    }
    dynamicContentEntrance.OwnerCustomerID = result.customerId;
    const ownerChange = dynamicContentEntrance.OwnerOrganisationID && dynamicContentEntrance.OwnerOrganisationID !== result.ownerOrganisationID;
    dynamicContentEntrance.OwnerOrganisationID = result.ownerOrganisationID ?? dynamicContentEntrance.OwnerOrganisationID;
    const query = { $or: [{ ibex3customerId: result.customerId }, { organisationCustomerId: result.customerId }] };
    const success = await this.setOwnerOrganisation(query, dynamicContentEntrance);
    if (success) {
      this.createOrUpdate(dynamicContentEntrance, ownerChange);
    }
  }

  public async setOwnerOrganisation(query, dynamicContentEntrance): Promise<any> {
    if (dynamicContentEntrance.OwnerOrganisationID) {
      return true;
    }
    const organisations = await this.organisationService.find(0, 100, query);
    if (organisations.data.length === 0) {
      return this.dialogService.confirm('No Organisation for customer found, do you like to create anyway ?');
    } else {
      if (organisations.data.length > 1) {
        console.log('more then one organisation has this customer assigned, we take first match');
      }
      dynamicContentEntrance.OwnerOrganisationID = organisations.data[0].OrganisationID.toString();
      return true;
    }
  }

  public async openConfigurationDialog(dynamicContentEntrance): Promise<any> {
    let dialogRef: MatDialogRef<CompositionWizardCustomerSelectComponent>;
    const data = dynamicContentEntrance ? {
      type: dynamicContentEntrance.type, id: dynamicContentEntrance.OwnerCustomerID, config: dynamicContentEntrance.config, readonly: dynamicContentEntrance.readonly,
      noCategories: dynamicContentEntrance.noCategories, ownerOrganisationID: dynamicContentEntrance.OwnerOrganisationID
    } : null
    const config: MatDialogConfig = { data: data };
    dialogRef = this.dialog.open(CompositionWizardCustomerSelectComponent, config);
    return dialogRef.afterClosed().toPromise();
  }

  public async createOrUpdate(dynamicContentEntrance, ownerChange = false) {
    if (dynamicContentEntrance._id) {
      const result = await this.dynamicContentEntranceService.update(dynamicContentEntrance._id, dynamicContentEntrance);
      if (!result) {
        return console.log('could not update');
      }
      this.save.emit();
      this.getEntrances();
      this.snackbar.success('Content updated');
      if (ownerChange) {
        this.updateOwnerOrganisationOnChildren(result.children, result.OwnerOrganisationID);
      }
    } else {
      const result = await this.dynamicContentEntranceService.create(dynamicContentEntrance);
      if (!result) {
        return console.log('could not create');
      }
      if (!this.composition.dynamicContentIds) {
        this.composition.dynamicContentIds = [];
      }
      this.composition.dynamicContentIds.push(result._id);
      this.save.emit();
      this.getEntrances();
      this.snackbar.success('Content created');
    }
  }

  public async updateOwnerOrganisationOnChildren(childIds, ownerOrganisationID, skip = 0) {
    if (!childIds?.length) {
      return;
    }
    const children = await this.dynamicContentService.find(skip, 500, { $or: [{ _id: { $in: childIds } }, { masterLanguageEntry: { $in: childIds } }] });
    if (!children?.data?.length) {
      return;
    }
    if (children.total - skip > children.data.length) {
      this.dynamicContentService.find(skip + 500, 500, { $or: [{ _id: { $in: childIds } }, { masterLanguageEntry: { $in: childIds } }] });
    }
    const ancestorIds = [];
    children.data.forEach(child => {
      if (child.children) {
        ancestorIds.concat(child.children);
      }
      if (child?.content?.products) {
        this.productService.patch(child.content.products, { OwnerOrganisationID: ownerOrganisationID });
      }
      this.dynamicContentService.patch(child._id, { OwnerOrganisationID: ownerOrganisationID });
    });
    if (ancestorIds.length) {
      return this.updateOwnerOrganisationOnChildren(ancestorIds, ownerOrganisationID);
    }
  }

  public async detail(event: DataTableEvent) {
    const organisation = await this.myOrganisationService.get(event.row.OwnerOrganisationID)
    if (!organisation) {
      return this.snackbar.open('you have no access to this organisation');
    }
    this.messengerService.setOrganisation(organisation);
    setTimeout(() => this.router.navigate([{ outlets: { org: organisation.name, primary: 'dynamic-content' } }], { queryParams: { entranceId: event.row._id } }), 1);
  }

  public edit(event: DataTableEvent) {
    this.updateOrCreateDynamicContent(event.row);
  }

  public async remove(event: DataTableEvent) {
    if (event?.row?.children?.length) {
      return this.snackbar.error('Failed to remove content: remove first all assigned children');
    }
    const result = await this.packageInstalledAppsService.find(0, 100, { dynamicContentEntranceId: event.row._id });
    if (result?.data?.length) {
      return this.snackbar.error('Failed to remove content: entrance is still connected to package');
    }
    const res = await this.dialogService.confirm('Remove Dynamic Content?');
    if (!res) {
      return;
    }
    await this.dynamicContentEntranceService.remove(event.row._id);
    this.save.emit();
    this.getEntrances();
    this.snackbar.success('Content removed');
  }

  private async getEntrances() {
    if (!this.composition?.dynamicContentIds?.length) {
      return;
    }
    const result = await this.dynamicContentEntranceService.find(0, 100, { _id: { $in: this.composition.dynamicContentIds } });
    this.dynamicContentEntrances = result.data;
    this.assignedContentEntranceService.db = this.dynamicContentEntrances;
    this.dynamicContentTable.refresh();
  }
}
